import { FC, useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useStoreContext } from 'store/app';
import { useTranslationsContext } from 'store/translations';
import { getRandomInt } from 'utils/getRandomInt';
import { OverlayStar } from 'components/layout/overlayStar';
import { Countdown } from 'components/core/countdown';
import { ReceivedPresent } from './ReceivedPresent';
import './OverlayExchange.styl';
import { ToastTypes } from 'src/types';

interface OverlayExchangeProps {
    onCancel: () => void;
    presentId: number;
}

const COUNT_TIMER = 30;

export const OverlayExchange: FC<OverlayExchangeProps> = observer(({ onCancel, presentId }) => {
    const { exchangeSnake, getCollectionBySnakeId, exchangedSnake, showToast, logOperation } = useStoreContext();
    const { translate } = useTranslationsContext();
    const randomInt = useRef(getRandomInt(5, 15));

    useEffect(() => {
        logOperation('collections', 'change_start', presentId);
    }, []);

    const onStopCountdown = (time?: { seconds: string; minutes?: string }) => {
        const isTimeOut = COUNT_TIMER - randomInt.current === (time && +time.seconds);

        if (isTimeOut) {
            exchangeSnake(presentId).catch((err) => {
                console.warn(err);
                showToast(translate('exchange.error'), ToastTypes.ERROR);
                onCancel();
            });
            randomInt.current = getRandomInt(5, 15);
        }

        return isTimeOut;
    };

    const collectionName = useMemo(() => {
        const collection = exchangedSnake && getCollectionBySnakeId(exchangedSnake.id);
        return collection?.custom_params.title ?? collection?.name;
    }, [exchangedSnake, getCollectionBySnakeId]);

    return (
        <OverlayStar>
            {collectionName && exchangedSnake?.custom_params.title ? (
                <ReceivedPresent
                    imgSrc={exchangedSnake.custom_params.img}
                    collectionName={collectionName}
                    presentName={exchangedSnake.custom_params.title}
                    onCloseModal={onCancel}
                />
            ) : (
                <div className="overlay-exchange">
                    <div className="overlay-exchange-countdown">
                        <Countdown timer={{ seconds: 30 }} onStop={onStopCountdown} />
                    </div>
                    <span className="overlay-exchange-title">{translate('exchange.title')}</span>
                    <span className="overlay-exchange-subtitle">{translate('exchange.subtitle')}</span>
                    <button
                        className="overlay-exchange-close-btn"
                        onClick={() => {
                            logOperation('collections', 'change_cancel', presentId);
                            onCancel();
                        }}
                    >
                        {translate('exchange.button.cancel')}
                    </button>
                </div>
            )}
        </OverlayStar>
    );
});
